import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createInquiry, deleteMyInquiry, getInquiries, getInquiry, getMyInquiries, replyInquiry } from "./requests";
import type { CustomQueryOptions, Inquiry, Pagination } from "../types";

export const useCreateInquiry = (onSuccess?: (data: any) => void, onError?: (error: Error) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {subject?: string; message: string; email: string; workspaceId: string}) => createInquiry(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['my-inquiries'], exact: false });
    }
  });
};

export const useGetInquiries = (filter: {
  page?: number; 
  limit?: number; 
  status?: 'pending' | 'resolved';
  sort?: 'asc' | 'desc';
}, options?: CustomQueryOptions<{data: Inquiry[], pagination: Pagination}>) => {

  return useQuery({
    queryKey: [`inquiries`, JSON.stringify(filter)],
    queryFn: () => getInquiries(filter),
    ...options
  });
};

export const useReplyInquiry = (onSuccess?: (data: any) => void, onError?: (error: Error) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {inquiryId: string, response: string}) => replyInquiry(data.inquiryId, { response: data.response }),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'], exact: false });
    }
  });
};

export const useGetInquiry = (inquiryId: string, options?: CustomQueryOptions<{data: Inquiry}>) => {
  return useQuery({
    queryKey: [`inquiry`, inquiryId],
    queryFn: () => getInquiry(inquiryId),
    ...options
  });
};

export const useGetMyInquiries = (userId: string, filter: {
  page?: number; 
  limit?: number; 
  status?: 'pending' | 'resolved';
  sort?: 'asc' | 'desc';
}, options?: CustomQueryOptions<{data: Inquiry[], pagination: Pagination}>) => {
  return useQuery({
    queryKey: [`my-inquiries`, JSON.stringify(filter)],
    queryFn: () => getMyInquiries(userId, filter),
    ...options
  });
};

export const useDeleteMyInquiry = (onSuccess?: (data: any) => void, onError?: (error: Error) => void) => {
  return useMutation({
    mutationFn: (inquiryId: string) => deleteMyInquiry(inquiryId),
    onSuccess,
    onError
  });
};